import { DomHandler } from 'primeng/dom';
import { DialogReturnData } from '../../../models/dialog-return-data.model';
import { MenuItemId } from '../../../enum/menu-item-id.enum';
import { DataAction } from '../../../enum/data-action.enum';
import { FormStatus } from '../../../enum/form-status.enum';
import { MenuItem } from 'primeng/api';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { SystemMessageService } from '../../../services/system-message.service';
import { SystemConfirmationService } from '../../../services/system-confirmation.service';
import { LoadingMaskService } from '../../../services/loading-mask.service';
import { LanguageService } from '../../../services/language.service';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { AutoCompleteStatus } from '../../../models/auto-complete-status.model';
import { ValidationService } from '../../../services/validation.service';
import { DOCUMENT } from '@angular/common';

/**
 * 基本表單的抽象類別，所謂的基本表單就是下方沒有 Table 呈現的單檔畫面。
 * 每個基本表單上方都會有 Menubar 來操作表單的狀態。
 * 如果不是資料維護的功能，是直接可以填寫送出的表單，也算是這種類型，只要繼承後設定不需要 Menubar 就可以。
 *
 * @export
 * @abstract
 * @class BaseFormComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  template: ''
})
export abstract class BaseFormComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * 這個 Component 的 class 名稱。
   * 用來當做 Table 狀態儲存到 sessionStorage 的 Key 值以及元件 id 的前綴。
   */
  abstract componentClassName: string;

  /**
   * 要不要產生單頭的功能選單。
   * 預設是 true，如果是查詢或是表單的功能再 override 覆寫成 false。
   */
  hasMasterMenuBar: boolean = true;

  /**
   * 這個功能是不是開在 Dialog 內。
   * 預設是 false。
   */
  isDialogMode: boolean = false;

  /**
   * 用來給 canDeactivate Router 判斷子 Dialog 是不是打開。
   * 主要是要防止打開子 Dialog 後，又按了瀏覽器的上一頁，
   * 這時候 Dialog 不會消失，但這個頁面會跑到上一頁。
   */
  dialogOpened: boolean = false;

  // ------------ 以下開始為單頭資料相關 properties -------------
  /**
   * 單頭的資料。
   * 如果希望寫程式可以點出提示，可以 override 這個自行定義資料型別。
   */
  masterData: any;

  /**
   * 單頭資料 Key 的欄位名稱。
   *
   * 如果不是在 Dialog 內開啟，直接開啟會透過 Router 傳入要看哪筆資料，
   * 所以 Router 設定的參數名稱和這個要一致才能正確抓到。
   *
   * 預設是 uuid。
   */
  masterDataKeyName: string = 'uuid';

  /**
   * 單頭資料 Key 欄位的值。
   * 預設是抓 uuid 的值。
   */
  masterDataKeyValue: string | null = '';

  // ------------ 以下開始為表單相關 properties -------------
  /**
   * 輸入表單的 FormGroup。
   * 在 initFormGroup() 才初始化。
   */
  formGroup!: FormGroup;

  /**
   * 用來記錄表單是否填寫完成按下送出。
   * 提供給畫面判斷要不要顯示欄位驗證錯誤訊息的條件之一。
   */
  submitted: boolean = false;

  /**
   * 用來控制切換語言的時候要重繪元件。
   * 因為有的元件在切換語言的時候不會即時變更顯示的文字，
   * 會透過這個變數的切換來先隱藏再顯示，達到重新繪製元件的效果。
   */
  langChangeVisible: boolean = true;

  /**
   * 修改前的 formGroup rawValue。
   * 取消編輯或重置時會直接用這個把值復原，不用重新再去後端查一次資料比較快，
   * 但風險就是資料可能不是最新的。
   */
  originalFormRawValue: any;

  /**
   * 放輸入欄位的物件。
   * 用在解決 PrimeNG focusTrap 在 Dialog 裡面會錯亂的問題。
   *
   * 要用 @ViewChild('contentElement 上 #的名稱') 去畫面取得物件。
   */
  @ViewChild('contentElement') contentElement?: ElementRef;

  /**
   * 按下送出儲存成功後，是否要重新查詢資料。
   * 預設是 false，因為這是後來加的參數，怕影響到既有程式。
   *
   * 如果要重新查詢該筆資料，就 override 改成 true。
   */
  reloadAfterSubmit: boolean = false;

  /**
   * 目前是否是在執行 reload
   * 預設是 false
   */
  isReload: boolean = false;

  // ------------ 以下開始為 autoComplete 控制相關 properties -------------
  /**
   * autoComplete 元件複製合法的值貼上後，如果不選擇任何選項就直接按動作按鈕，
   * 該欄位的值在畫面上會看到，但動作時從 formGroup 取值會取到修改前的值，
   * 要按第二次動作按鈕時才會取到正確的值。
   *
   * 因為貼上值後會去驗證這個值是不是存在的值，完成驗證後才會把值寫進 formGroup，
   * 貼上後直接按其他動作的按鈕不會等這個事件結束，所以無法正確取到值。
   *
   * 為了解決這個問題，任何按鈕動作時要訂閱 autoComplete 完成的事件，確定做完後才進行動作。
   */
  autoCompleteStatus: AutoCompleteStatus = new AutoCompleteStatus();

  // ------------ 以下開始為功能選單相關 properties -------------
  /**
   * 單頭功能選單。
   * 在 initMasterMenu() 才初始化。
   */
  masterMenu: MenuItem[] = [];

  /**
   * 單頭的狀態。
   * 通常用在控制功能選單按鈕狀態。
   * FormStatus.VIEW: 瀏覽 / FormStatus.EDIT: 編輯
   * 預設都是瀏覽狀態，如果是查詢或是表單的功能再 override 覆寫成 FormStatus.EDIT。
   */
  masterFormStatus: FormStatus = FormStatus.VIEW;

  /**
   * 前一個畫面傳入要對資料做什麼操作。
   * 通常用在決定要怎麼取資料，和結束後要回傳怎麼樣的資料的判斷。
   * 預設是檢視。
   */
  parentDataAction: DataAction = DataAction.VIEW;

  /**
   * 關閉 Dialog 的時候要通知前一個畫面資料做了什麼改變。
   * 前一個畫面會根據這個來改變畫面資料的呈現。
   * 預設是放棄。
   */
  dialogReturnDataAction: DataAction = DataAction.DISCARD;

  // ------------ 以下開始為 Subject -------------
  /**
   * 用來取消訂閱
   */
  readonly _destroying$ = new Subject<void>();

  /**
   * 目前捲軸的位置
   */
  currentScrollPosition: number = 0;

  protected formBuilder = inject(FormBuilder);
  protected languageService = inject(LanguageService);
  protected loadingMaskService = inject(LoadingMaskService);
  protected translateService = inject(TranslateService);
  protected systemConfirmationService = inject(SystemConfirmationService);
  protected systemMessageService = inject(SystemMessageService);
  protected validationService = inject(ValidationService);
  protected activatedRoute = inject(ActivatedRoute);
  protected dynamicDialogRef = inject(DynamicDialogRef);
  protected dynamicDialogConfig = inject(DynamicDialogConfig);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected elementRef = inject(ElementRef);
  protected document = inject(DOCUMENT);

  /**
   * init 的流程:
   * (1) 顯示載入中 Mask。
   * (2) 呼叫 initFormGroup - 初始化 FormGroup 欄位。
   * (3) 呼叫 initStaticOptions - 初始化靜態的下拉選單選項。
   * (4) 把所有欄位都 disable。
   * (5) 呼叫 initFormData - 載入所有 Form 所需要的資料。
   * (5-1) 成功載入，呼叫 afterFormDataLoadSuccess - 載入資料後的事，子元件要自己實作。
   * (5-2) 載入失敗，呼叫 afterFormDataLoadFail - 載入失敗後的事，子元件要自己實作。
   */
  ngOnInit(): void {
    console.log('BaseFormComponent ngOnInit');

    // 一開啟畫面就顯示載入中，等所有 init 都完成才可以開始操作
    // 因為資料沒載完全就先可以用，最後也會是錯的
    this.loadingMaskService.show();

    // 判斷是否是在 Dialog 內開啟的
    this.isDialogMode = this.dynamicDialogConfig.data ? true : false;
    if (this.isDialogMode) {
      // 取 dynamicDialogConfig data dataAction 來定義前一個畫面要對資料做麼事情
      // 如果沒傳預設就還是 DataAction.VIEW
      this.parentDataAction = this.dynamicDialogConfig.data?.dataAction ?? DataAction.VIEW;

      // 取出單頭的 key 值
      this.masterDataKeyValue = this.dynamicDialogConfig.data[this.masterDataKeyName];
    }

    this.initFormGroup();
    this.initStaticOptions();

    this.doLoadFormData();
  }

  /**
   * 初始化完 Component View 及 Child Component View 之後呼叫。
   */
  ngAfterViewInit(): void {
    console.log('ngAfterViewInit');
    // 找出日曆和下拉選單的按鈕，把這些按鈕的 tabindex 改為 -1。
    if (this.contentElement) {
      const contentHTMLElement = this.contentElement.nativeElement as HTMLElement;
      this.setTabindex(contentHTMLElement);
    }

    this.setContentElementIndex();
  }

  /**
   * 設定 bpaas-content-element class 的所有元件內的日曆和下拉選單的按鈕 tabindex 改為 -1。
   */
  setContentElementIndex(): void {
    // 從整個畫面找到有設定 bpaas-content-element class 的元件
    const contentElements = this.elementRef.nativeElement.querySelectorAll('.bpaas-content-element');
    if (contentElements.length > 0) {
      // 找有設定 bpaas-content-element class 的所有元件內的日曆和下拉選單的按鈕 tabindex 改為 -1。
      contentElements.forEach((element: Element) => {
        this.setTabindex(element as HTMLElement);
      });
    }
  }

  /**
   * 找出日曆和下拉選單的按鈕，把這些按鈕的 tabindex 改為 -1。
   * @param contentHTMLElement 找哪個元件下的
   */
  private setTabindex(contentHTMLElement: HTMLElement): void {
    const buttonElements = contentHTMLElement.querySelectorAll('button.p-datepicker-trigger, button.p-autocomplete-dropdown');
    if (buttonElements) {
      buttonElements.forEach(element => {
        element.setAttribute('tabindex', '-1');
      });
    }
  }

  /**
   * 初始化表單 FormGroup 欄位。
   * 只定義 Form 的欄位和需要做的驗證就好，要取值設定在 initFormData() 裡做。
   *
   * 如果是 inline edit 的 FormGroup init 也一併寫在這裡。
   */
  abstract initFormGroup(): void;

  /**
   * 初始化表單靜態的下拉選單 ( 選項不是從後端取的 )。
   *
   * 如果畫面沒有下拉選單的欄位，實作後直接 return 就可以。
   */
  abstract initStaticOptions(): void;

  /**
   * 載入畫面所需的表單資料。
   * 這個會在 ngOnInit() 的時候呼叫，在放棄編輯或重置的時候也會來呼叫，重新復原畫面上的資料。
   */
  doLoadFormData(): void {

    // 這裡可能會覺得跟 ngOnInit 重複做了 mask，但如果是重取資料的時候還是需要 mask 的。
    this.loadingMaskService.show();

    // 先把 Form 欄位都 disable
    // emitEvent: false 是因為不希望觸發到 valueChange 事件，
    // 避免有的程式會寫 valueChange 事件做資料改變或欄位 disable 的控制被這個觸發。
    this.formGroup.disable({ emitEvent: false });

    this.initFormData().then(() => {
      this.doFormDataLoadSuccess();
      this.afterFormDataLoadSuccess();
    }).catch(response => {
      console.error(response);
      this.systemMessageService.error(response);
      this.doFormDataLoadFail();
      this.afterFormDataLoadFail();
    }).finally(() => {
      this.isReload = false;
      this.loadingMaskService.hide();
    });
  }

  /**
   * 設定表單所有需要的資料。
   * 通常會包含以下內容 :
   * (1) 一些需要從 DB 查詢的，像是單頭、單身資料或權限之類的等等。
   * (2) 動態的下拉選單要查出選項內容。
   * (3) 根據欄位資料決定的下拉選單內容。
   *
   * 注意 :
   * (1) 因為 'async' 修飾元無法與 'abstract' 修飾元並用，實作時請自己加上 async。
   * (2) 透過 async / await 保證執行的順序，或用 Promise.all 非同步同時執行多個優化處理時間。
   * (3) 請一定要把取回的資料寫入 this.masterData。
   */
  abstract initFormData(): Promise<any>;

  /**
   * Form Data 全部都成功載入後預設要做的事。
   * (1) 訂閱切換語言別的通知。
   * (2) 初始化單頭功能選單
   * (3) 如果是新增或編輯時直接進入編輯狀態。
   */
  doFormDataLoadSuccess(): void {
    console.log('BaseFormComponent doFormDataLoadSuccess');
    // 把載入的 Form Data 保存下來
    this.originalFormRawValue = this.formGroup.getRawValue();
    this.initLangChangeSubscribe();

    if (this.hasMasterMenuBar) {
      // 初始化單頭功能選單
      this.initMasterMenu();
      this.initExtraMasterMenu();
      this.setExtraMasterMenuStatus();
      this.redrawMasterMenu();
    }

    // 存檔後畫面重新整理這段會再跑到，這時候不可以進入編輯，要特別判斷這個情境
    if (!this.masterDataKeyValue // 等於前一個畫面要新增，所以不會傳 key 欄位的值進來，就是要直接進入編輯
      || this.masterDataKeyValue == DataAction.NEW  // 這跟上一個一樣是新增，但不是在 Dialog 的狀況，會去抓 Router 上傳入的 key 欄位值，如果是 new 就是要直接進入編輯
      || this.masterFormStatus == FormStatus.EDIT // 一開啟就是編輯狀態 masterFormStatus = Edit，例如 : 基本表單或是查詢
      || (!this.isReload && this.parentDataAction == DataAction.EDIT)) { // 如果不是重新整理，前一個畫面要編輯
        // 直接觸發編輯按鈕進入編輯狀態
        this.masterMenuEditCommand();
    }
  }

  /**
   * 訂閱切換語言別的通知。
   */
  private initLangChangeSubscribe(): void {
    this.translateService.onLangChange.pipe(
      takeUntil(this._destroying$)
    ).subscribe((event: LangChangeEvent) => {
      this.doLangChange();
      this.afterLangChange();
      // 語系切換的時候部分元件畫面不會重置, 所以透過先隱藏再顯示來重置
      this.langChangeVisible = false;
      this.changeDetectorRef.detectChanges();
      this.langChangeVisible = true;
    });
  }

  /**
   * 預設的語言變更時要做的事情。
   * (1) 變更 Master 功能選單語系。
   */
  doLangChange(): void {
    // 變更 Master 功能選單語系
    this.languageService.processMenuTranslation(this.masterMenu);
    this.redrawMasterMenu();
  }

  /**
   * 額外需要在語系切換時做的事情。
   * 通常是改變下拉選單的語系。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterLangChange(): void;

  /**
   * Form Data 全部都成功載入後額外要做的事。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterFormDataLoadSuccess(): void;

  /**
   * Form Data 載入過程有發生錯誤預設要做的事。
   * (1) 關閉所有表單欄位。
   * (2) 所有單頭功能選單都不能按。
   */
  doFormDataLoadFail(): void {
    console.log('BaseFormComponent doFormDataLoadFail');
    // 把 Form 欄位都 disable
    this.formGroup.disable({ emitEvent: false });

    if (this.hasMasterMenuBar) {
      // 如果載入資料失敗，所有按鈕都不能按
      this.masterMenu.forEach(menuItem => {
        menuItem.disabled = true;
      });
      this.redrawMasterMenu();
    }
  }

  /**
   * Form Data 載入過程有發生錯誤額外要做的事。
   * 如果相關資料有其中一個載入失敗就不能繼續動作，因為不齊全的資料再繼續動作會有問題。
   * 應該要把相關的功能都 disable 不能用。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterFormDataLoadFail(): void;

  /**
   * 初始化單頭功能選單。
   * (1) 編輯。
   * (2) 放棄。
   * (3) 送出。
   * (4) 刪除。
   */
  initMasterMenu(): void {
    console.log('BaseFormComponent initMasterMenu');
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key
    this.masterMenu = [
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_EDIT),
        label: this.translateService.instant(MenuItemId.I18N_EDIT),
        automationId: MenuItemId.I18N_EDIT,
        icon: 'pi pi-fw pi-pencil',
        command: () => {
          this.masterMenuEditCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_DISCARD),
        label: this.translateService.instant(MenuItemId.I18N_DISCARD),
        automationId: MenuItemId.I18N_DISCARD,
        icon: 'pi pi-fw pi-times',
        command: () => {
          this.masterMenuDiscardCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_SUBMIT),
        label: this.translateService.instant(MenuItemId.I18N_SUBMIT),
        automationId: MenuItemId.I18N_SUBMIT,
        icon: 'pi pi-fw pi-save',
        command: () => {
          this.masterMenuSubmitCommand();
        }
      },
      {
        id: this.parseToId(MenuItemId.MASTER_MENU_DELETE),
        label: this.translateService.instant(MenuItemId.I18N_DELETE),
        automationId: MenuItemId.I18N_DELETE,
        icon: 'pi pi-fw pi-trash',
        command: () => {
          this.masterMenuDeleteCommand();
        }
      }
    ];

    this.setMasterMenuStatus(FormStatus.VIEW);
  }

  /**
   * 單頭是否在瀏覽狀態。
   * @returns 單頭是否在瀏覽狀態
   */
  masterInViewStatus(): boolean {
    return this.masterFormStatus == FormStatus.VIEW;
  }

  /**
   * 設定單頭功能選單各按鈕的狀態。
   * @param formStatus 單頭表單的狀態
   */
  setMasterMenuStatus(formStatus: FormStatus): void {
    this.masterFormStatus = formStatus;

    // 編輯按鈕，在瀏覽狀態才能按
    let masterMenuEdit = this.getMasterMenuItem(MenuItemId.MASTER_MENU_EDIT);
    if (masterMenuEdit) {
      masterMenuEdit.disabled = !this.masterInViewStatus();
    }

    // 放棄按鈕，在編輯狀態才會出現、才能按
    let masterMenuDiscard = this.getMasterMenuItem(MenuItemId.MASTER_MENU_DISCARD);
    if (masterMenuDiscard) {
      masterMenuDiscard.disabled = this.masterInViewStatus();
      masterMenuDiscard.visible = !this.masterInViewStatus();
    }

    // 送出按鈕，在編輯狀態才會出現、才能按
    let masterMenuSubmit = this.getMasterMenuItem(MenuItemId.MASTER_MENU_SUBMIT);
    if (masterMenuSubmit) {
      masterMenuSubmit.disabled = this.masterInViewStatus();
      masterMenuSubmit.visible = !this.masterInViewStatus();
    }

    // 刪除按鈕，在瀏覽狀態才能按
    let masterMenuDelete = this.getMasterMenuItem(MenuItemId.MASTER_MENU_DELETE);
    if (masterMenuDelete) {
      masterMenuDelete.disabled = !this.masterInViewStatus();

      // 在 Dialog 內開啟才顯示刪除
      // 因為如果從某個連結直接開啟，又可以刪除，那刪除結束後要怎麼呈現? 停在這頁很怪，要導到哪頁也不知道
      masterMenuDelete.visible = this.isDialogMode;
    }

    this.setExtraMasterMenuStatus();
    this.redrawMasterMenu();
    this.afterMasterMenuStatusChange();
  }

  /**
   * 單頭功能選單 - Edit 按下去後預設要做的事情。
   * (1) 進入編輯狀態。
   * (2) 打開所有表單欄位。
   * (3) 呼叫 initFormControlDisabled() 控制欄位狀態。
   */
  private masterMenuEditCommand(): void {
    // 單頭狀態切成編輯狀態
    this.setMasterMenuStatus(FormStatus.EDIT);
    // 打開所有欄位
    console.log('BaseFormComponent formGroup enable');
    // 把 Form 欄位都 enable
    this.formGroup.enable({ emitEvent: false });
    // 欄位自訂可不可以輸入的狀態改變
    this.initFormControlDisabled();

    // 自動 Focus 第一個可以進入的欄位
    setTimeout(() => {
      if (this.contentElement) {
        const contentHTMLElement = this.contentElement.nativeElement as HTMLElement;
        this.focusElement(contentHTMLElement);
      } else {
        const contentElements = this.elementRef.nativeElement.querySelectorAll('.bpaas-content-element');
        if (contentElements.length > 0) {
          const contentHTMLElement = contentElements[0] as HTMLElement;
          this.focusElement(contentHTMLElement);
        }
      }
    }, 0);
  }

  private focusElement(contentHTMLElement: HTMLElement): void {
    const focusableElements = DomHandler.getFocusableElements(contentHTMLElement);
    if (focusableElements) {
      focusableElements[0].focus();
    }
  }

  /**
   * Form Group 表單欄位的 disable 狀態。
   * 欄位的 disable 狀態請在這裡面設定，不要在 initFormGroup() 設定。
   *
   * 因為進入編輯狀態的時候，this.formGroup.enable(); 會把全部欄位都打開。
   * 然後再呼叫這個來設定欄位 disable 狀態。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract initFormControlDisabled(): void;

  /**
   * 單頭功能選單 - Discard 按下去後要先做的事情
   * 這個目前是設計用來處理雙檔取消後 inline edit 也要跟著取消
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract beforeMasterMenuDiscard(): Promise<boolean>;

  /**
   * 單頭功能選單 - Discard 按下去後預設要做的事情
   * (1) 跳出確認視窗確認是否不保存資料。
   * (2) 執行 beforeMasterMenuDiscard
   * (3) 單頭狀態切回瀏覽狀態。
   * (4) 清空單頭資料。
   * (5) 如果是新增資料，放棄後直接關閉 Dialog。
   * (6) 如果不是新增資料，顯示處理中遮罩。
   * (7) 呼叫 doLoadFormData() 重新取資料復原畫面。
   */
  async masterMenuDiscardCommand(): Promise<void> {
    this.systemConfirmationService.confirmDiscardChanges(async() => {
      // 先做 before 事件
      const canDiscard = await this.beforeMasterMenuDiscard();

      if (!canDiscard) {
        return;
      }

      // 單頭狀態切回瀏覽狀態
      this.setMasterMenuStatus(FormStatus.VIEW);

      // 先把欄位都清空
      this.formGroup.reset();

      // 如果是新增資料，放棄後直接關閉 Dialog
      if (!this.masterDataKeyValue) {
        this.closeDialog();
      } else {
        this.loadingMaskService.show();
        // 重取資料
        this.doLoadFormData();
      }
    });
  }

  /**
   * 單頭功能選單 - Submit 按下去後要先做的事情
   * 這個目前是設計用來處理雙檔存檔前，要先做 inline edit 的驗證與儲存
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   *
   * 注意 :
   * (1) 因為 'async' 修飾元無法與 'abstract' 修飾元並用，實作時請自己加上 async。
   * (2) 透過 async / await 保證執行的順序，或用 Promise.all 非同步同時執行多個優化處理時間。
   */
  abstract beforeMasterMenuSubmit(): Promise<boolean>;

  /**
   * 單頭功能選單 - Submit 按下去預設後要做的事情。
   * (1) 先做前處理過了才繼續。
   * (2) 進行欄位驗證。
   * (3) 欄位驗證過後，顯示處理中遮罩。
   * (4) 呼叫 doMasterMenuSubmit()，處理實際送出要做的事情。
   * (5) 處理完後呼叫 afterMasterMenuSubmitted()。
   */
  private async masterMenuSubmitCommand(): Promise<void> {
    const canSubmit = await this.beforeMasterMenuSubmit();
    if (!canSubmit) {
      return;
    }

    this.loadingMaskService.show();
    this.submitted = true;
    console.log('masterMenuSubmitCommand formGroup = ' + JSON.stringify(this.formGroup.getRawValue()));
    if (this.formGroup.valid) {
      this.waitAutoCompleteCompleted(
        async() => {
          // complete 後要實際去執行 doMasterMenuSubmit()
          const submitted = await this.doMasterMenuSubmit();
          if (submitted) {
            this.afterMasterMenuSubmitted();
          } else {
            this.loadingMaskService.hide();
          }
        }
      );
    } else {
      this.loadingMaskService.hide();
    }
  }

  /**
   * 要自己實作實際 Submit 的事件。
   *
   * 注意：
   * 請一定要把實際送出存檔後的資料寫入 this.masterData，因為關閉 Dialog 後會把資料回傳到前面顯示。
   */
  abstract doMasterMenuSubmit(): Promise<boolean>;

  /**
   * 單頭功能選單 - Submit 完畢後要做的事情。
   * (1) 單頭狀態切回瀏覽狀態。
   * (2) 把所有表單欄位 disable。
   * (3) 移除處理中遮罩。
   * (4) 顯示新增或修改成功訊息。
   */
  private afterMasterMenuSubmitted(): void {
    // 單頭狀態切回瀏覽狀態
    this.setMasterMenuStatus(FormStatus.VIEW);
    // 把 Form 欄位都 disable
    this.formGroup.disable({ emitEvent: false });
    // 只要有存檔成功，就用目前的 form 資料寫回 originalFormRawValue
    this.originalFormRawValue = this.formGroup.getRawValue();
    // console.log('afterMasterMenuSubmitted this.originalFormRawValue = ' + JSON.stringify(this.originalFormRawValue));

    // 要把 key 值設成新的，這樣重抓的時候才能查回資料
    if (this.isDialogMode) {
      // this.dynamicDialogConfig.data[this.masterDataKeyName] = this.masterData[this.masterDataKeyName];
      this.masterDataKeyValue = this.masterData[this.masterDataKeyName];
    }

    this.loadingMaskService.hide();
    // 如果前面傳進來是要做 Add，儲存代表做完新增。如果是 View，會跑到儲存代表做完修改。
    if (this.parentDataAction == DataAction.NEW) {
      // 只要沒有關閉 Dialog 的狀態，前面傳進來是要做 Add，儲存後又再編輯，再送出，要通知前一個畫面也還是新增資料。
      this.dialogReturnDataAction = DataAction.NEW;
      this.systemMessageService.success(this.originalFormRawValue.id == DataAction.NEW
         ? 'message.detail.insert-success' : 'message.detail.update-success');
    } else {
      this.dialogReturnDataAction = DataAction.EDIT;
      this.systemMessageService.success('message.detail.update-success');
    }

    // 如果有設定要重取資料，呼叫 doLoadFormData 重取資料
    // 但實作的 doMasterMenuSubmit 儲存成功後一定要回寫 key 值到 masterData 內
    if (this.reloadAfterSubmit) {
      this.isReload = true;
      this.doLoadFormData();
    }
  }

  /**
   * 單頭功能選單 - Delete 按下去後預設要做的事情。
   * (1) 跳出確認視窗確認是否要刪除資料。
   * (2) 確認要刪除資料的話，顯示處理中遮罩。
   * (3) 呼叫 doMasterMenuDelete()，處理實際刪除要做的事情。
   * (4) 處理完後呼叫 afetrMasterMenuDeleted()。
   */
  private masterMenuDeleteCommand(): void {
    this.systemConfirmationService.confirmDelete(
      async() => {
        this.loadingMaskService.show();
        const deleted = await this.doMasterMenuDelete();
        if (deleted) {
          this.afetrMasterMenuDeleted();
        }
      }
    );
  }

  /**
   * 要自己實作實際刪除的事件。
   */
  abstract doMasterMenuDelete(): Promise<boolean>;

  /**
   * 單頭功能選單 - Delete 刪除完畢後要做的事情。
   * (1) 移除處理中遮罩。
   * (2) 處理 Dialog 回傳資料。
   * (3) 關閉 Dialog。
   * (4) 顯示刪除成功訊息。
   */
  private afetrMasterMenuDeleted(): void {
    this.loadingMaskService.hide();
    // 如果前面傳進來是要做 New，刪除代表做完新增又刪掉等於沒做改變。
    // 如果是 View，刪除代表要刪資料。
    this.dialogReturnDataAction = (this.parentDataAction == DataAction.NEW) ? DataAction.DISCARD : DataAction.DELETE;
    this.closeDialog();
    this.systemMessageService.success('message.detail.delete-success');
  }

  /**
   * 初始化單頭額外的功能選單。
   *
   * 如果沒有額外的按鈕，實作後直接 return 就可以。
   */
  abstract initExtraMasterMenu(): void;

  /**
   * 設定單頭額外的功能選單按鈕狀態，
   * 或是有特殊條件要改變預設的功能狀態 (ex: 某些條件不能編輯或刪除)。
   * 這個會在初始化單頭額外的功能選單後以及每次單頭狀態改變後呼叫。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract setExtraMasterMenuStatus(): void;

  /**
   * 重繪單頭功能選單。
   */
  redrawMasterMenu(): void {
    this.masterMenu = [...this.masterMenu];
  }

  /**
   * 單頭功能選單按了之後會觸發 setMasterMenuStatus()，如果在這之後有要額外做事情可以寫在這裡。
   * 這個通常是為了給雙檔用的，因為雙檔的功能按鈕控制會跟著單頭的狀態改變。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterMasterMenuStatusChange(): void;

  /**
   * 取得指定的單頭功能選單。
   * @param menuItemId 功能選單 id，不需要 parseToId
   * @returns 功能選單
   */
  getMasterMenuItem(menuItemId: string): MenuItem | undefined {
    return this.masterMenu.find(menuItem => menuItem.id == this.parseToId(menuItemId));
  }

  /**
   * formControl 共用 function，用來提供 html 判斷欄位是否有錯誤。
   * @param formControlName formControl 的名稱
   * @returns boolean 欄位是否有錯誤
   */
  formControlInvalid(formControlName: string): boolean {
    const formControl = this.formGroup.get(formControlName);
    if (formControl) {
      return formControl.invalid && (formControl.dirty || this.submitted);
    } else {
      return false;
    }
  }

  /**
   * formControl 共用 function，用來提供 html 取 FormControl。
   * @param formControlName formControlNameformControl 的名稱
   * @returns FormControl
   */
  formControl(formControlName: string): FormControl {
    return this.formGroup.get(formControlName) as FormControl;
  }

  /**
   * 清除 FormControl 值。
   * @param formControlName formControl 的名稱
   * @param formGroup 哪個 formGroup，可以不傳，不傳預設是單頭的 formGroup
   */
  clearFormControlValue(formControlName: string, formGroup?: FormGroup): void {
    const thisFormGroup = formGroup ? formGroup : this.formGroup;
    const formControl = thisFormGroup.get(formControlName);
    if (formControl) {
      formControl.setValue(null);
    }
  }

  /**
   * autoComplete 欄位進入時先把當下的值保存下來。
   * @param event
   */
  autoCompleteFocus(event: any) {
    const key = this.getAutoCompleteKey(event.target.id);
    this.autoCompleteStatus.autoCompletePreviousValue.set(key, event.target.value.trim());
    // console.log('autoCompleteFocus key = ' + key + ', event.target.value = ' + event.target.value + ', autoCompleteCompleteValue = ' + this.autoCompleteStatus.autoCompleteCompleteValue.get(key) + ', autoCompletePreviousValue = ' + this.autoCompleteStatus.autoCompletePreviousValue.get(key));
    this.setAutoCompleteIsCleared();
  }

  /**
   * 把按下清除的標記設成 false。
   */
  private setAutoCompleteIsCleared() {
    this.autoCompleteStatus.isClearedFormControlName = '';
    this.changeDetectorRef.detectChanges();
  }

  /**
   * autoComplete 離開的時候，判斷如果有輸入值，且跟進入前不一樣，就要把 waitAutoComplete 改為 true。
   * @param event
   */
  autoCompleteBlur(event: any): void {
    const key = this.getAutoCompleteKey(event.target.id);
    this.setAutoCompleteIsCleared();
    if (event.target.value.trim()
      && (this.autoCompleteStatus.autoCompleteCompleteValue.get(key) !== undefined)
      && event.target.value.trim() != this.autoCompleteStatus.autoCompleteCompleteValue.get(key)
      && event.target.value.trim() != this.autoCompleteStatus.autoCompletePreviousValue.get(key)) {
      this.autoCompleteStatus.waitAutoComplete.set(key, true);
    } else {
      this.autoCompleteStatus.waitAutoComplete.set(key, false);
    }
  }

  /**
   * autoComplete 輸入資料要把按下清除的標記設成 false。
   * 用來解決貼上唯一值後馬上按刪除，值又會被帶回來的問題。
   * @param event
   */
  autoCompleteKeyUp(event: any): void {
    this.setAutoCompleteIsCleared();
  }

  /**
   * autoComplete Select 呼叫這個，把 waitAutoComplete 改為 false，並通知 autoCompleteSubject 已經完成。
   * 這個除了手動選擇會觸發外，forceSelection 查到符合的唯一值也會觸發。
   *
   * @param formControlName formControl 的名稱
   * @param formGroup 哪個 formGroup，可以不傳，不傳預設是單頭的 formGroup
   */
  autoCompleteCompleted(formControlName: string, formGroup?: FormGroup): void {
    if (formControlName == this.autoCompleteStatus.isClearedFormControlName) {
      // 如果是按了清除，不能把值再帶回來，要清空
      this.clearFormControlValue(formControlName, formGroup);
      this.setAutoCompleteIsCleared();
    }

    const thisFormGroup = formGroup ? formGroup : this.formGroup;
    const formControl = thisFormGroup.get(formControlName);
    const key = this.parseToId(formControlName);
    if (formControl) {
      // console.log('autoCompleteCompleted key = ' + key + ', completeValue = ' + formControl.getRawValue()?.label);
      this.autoCompleteStatus.autoCompleteCompleteValue.set(key, formControl.getRawValue()?.label);
    }

    this.resetAndCheckWaitAutoComplete(key);
  }

  /**
   * 清除 waitAutoComplete 需要等待完成的標記
   *
   * @param key
   */
  resetAndCheckWaitAutoComplete(key?: string): void {
    if (key) {
      this.autoCompleteStatus.waitAutoComplete.set(key, false);
    }

    // 檢查是否所有 waitAutoComplete 都為 false
    const allFalse = Array.from(this.autoCompleteStatus.waitAutoComplete.values()).every(value => value === false);
    // console.log('allFalse = ' + allFalse);
    // this.autoCompleteStatus.waitAutoComplete.forEach((value, key) => {
    //   console.log(`key: ${key}, value: ${value}`);
    // });
    if (allFalse) {
      // 清空 Map
      this.autoCompleteStatus.autoCompletePreviousValue.clear();
      this.autoCompleteStatus.autoCompletePreviousValue.clear();
      this.autoCompleteStatus.waitAutoComplete.clear();
      // 如果所有的 waitAutoComplete 都是 false，代表沒有欄位需要等待了，就發出完成通知
      this.autoCompleteStatus.autoCompleteSubject$.next();
    }
  }

  /**
   * autoComplete 清除後呼叫這個，除了把值清空外，
   * 把 waitAutoComplete 改為 false，並通知 autoCompleteSubject 已經完成。
   * 這個除了手動清空會觸發外，forceSelection 查不到符合的值也會觸發。
   *
   * @param formControlName formControl 的名稱
   * @param formGroup 哪個 formGroup，可以不傳，不傳預設是單頭的 formGroup
   */
  autoCompleteCleared(formControlName: string, formGroup?: FormGroup): void {
    const key = this.parseToId(formControlName);
    this.autoCompleteStatus.isClearedFormControlName = formControlName;
    this.changeDetectorRef.detectChanges();

    const thisFormGroup = formGroup ? formGroup : this.formGroup;
    const formControl = thisFormGroup.get(formControlName);
    if (formControl) {
      formControl.setValue(null);
      this.autoCompleteStatus.autoCompleteCompleteValue.set(key, '');
    }

    this.resetAndCheckWaitAutoComplete(key);
  }

  /**
   * 處理 autoComplete 貼上後馬上按功能按鈕會取不到該欄位的值。
   * 如果有按鈕按下去後，要取的欄位有包含自動完成欄位，按鈕應該要透過這個 function 處理後，再做該做的事。
   * @param callback 原本按鈕按下去後該做的事
   */
  waitAutoCompleteCompleted(callback: Function) {
    // 如果沒有訂閱過 autoAompleteSubject 才做訂閱
    if (!this.autoCompleteStatus.autoCompleteSubject$.observed) {
      this.autoCompleteStatus.autoCompleteSubject$.pipe(
        take(1)
      ).subscribe(() => {
        // complete 後要才實際去執行
        callback();
      });
    }

    this.resetAndCheckWaitAutoComplete();
  }

  /**
   * 元件 id 轉換，會將傳入的值前面串上元件的名稱且轉成全小寫，如果有大寫駝峰單詞轉成用 dash 區隔。
   *
   * @param idName id 名稱 (不需要自己加元件或功能前綴)
   * @param optionKey 非必須，通常是用在傳入自動完成的選項值，這個值只會轉成小寫，不會做駝峰轉換
   * @returns 元件名稱 + id 全小寫名稱 (駝峰單詞轉成用 dash 區隔)
   */
  parseToId(idName: string, optionKey?: string): string{
    let returnedId = this.componentClassName.charAt(0).toLocaleLowerCase() + this.componentClassName.slice(1) + '-' + idName;
    if (optionKey) {
      return returnedId.replace(/([A-Z])/g,"-$1").toLowerCase() + '-' + optionKey.toLowerCase();
    } else {
      return returnedId.replace(/([A-Z])/g,"-$1").toLowerCase();
    }
  }

  /**
   * 透過 event target 的 id 去找出 AutoComplete 記錄的 key
   * 主要是要把單身的 id 後面的-數字拿掉
   *
   * @param inputId event target 的 id
   * @returns 主要是要把單身的 id 後面的-數字拿掉
   */
  getAutoCompleteKey(inputId: string): string {
    const autoCompleteKey = inputId.replace(/-\d+$/, '');
    return autoCompleteKey;
  }

  /**
   * 關閉 Dialog。
   * 是被開在 Dialog 裡的才有效，就是關閉自己。
   */
  closeDialog(): void {
    if (!this.isDialogMode) {
      return;
    }

    console.log('BaseFormComponent closeDialog');
    // 如果這個程式是被開在 Dialog 內，close 後要回傳的資料
    const returnedData: DialogReturnData = {
      dialogReturnDataAction: this.dialogReturnDataAction,
      returnData: this.masterData
    }
    this.dynamicDialogRef.close(returnedData);
  }

  /**
   * 處理 scrollbar 錯亂問題。
   *
   * 因為在第一個 Dialog 未關閉的狀況下，裡面如果出現提示訊息，
   * 或是又開另一個 Dialog，關閉後面開啟的這個後，畫面會出現另一條 scrollbar。
   * 這個是為了把第二條 scrollbar 取消。
   */
  detailDialogHide(): void {
    setTimeout(() => {
      if (this.isDialogMode && DomHandler.findSingle(document.body, 'p-dynamicdialog')) {
        // 每隔 50 毫秒去偵測一次 body 的 tag 裡面 class 'p-overflow-hidden' 是不是被拿掉了
        // 如果被拿掉就要把他加回來
        let id = setInterval(() => {
          if (!DomHandler.hasClass(document.body, 'p-overflow-hidden') && DomHandler.findSingle(document.body, 'p-dynamicdialog')) {
            DomHandler.addClass(document.body, 'p-overflow-hidden');
          }
        }, 50);

        // 1 秒後停止偵測
        setTimeout(() => {
          clearInterval(id);
        }, 1000);
      }
    }, 0);
  }

  /**
   * 記錄目前 Scrollbar 位置，並把 Body 捲軸移到最上方。
   * 這個只有在最外層要開啟第一層的 Dialog，且裡面有日期欄位才需要做。
   */
  setScrollBeforeOpenDialog(): void {
    // 取得目前 body 捲軸的位置
    this.currentScrollPosition = window.scrollY;
    // body 捲軸捲回最上面
    window.scrollTo(0, 0);
  }

  /**
   * 還原 Body 捲軸位置。
   * 這個只有在最外層要開啟第一層的 Dialog，且裡面有日期欄位，被關閉時才需要做。
   */
  restoreScroll(): void {
    // 把 body 捲軸捲回原來位置
    setTimeout(() => {
      let scrollElement = this.document.defaultView!;
      scrollElement.scroll({
        top: this.currentScrollPosition,
        behavior: 'auto'
      });
    }, 0);
  }

  /**
   * 設定資料已經被異動。
   *
   * 主要是用在進入畫面後，可以直接按按鈕改資料的狀態。
   * 要自行呼叫這個設定資料有被異動過，這樣才能把更新資料往前面的畫面帶。
   */
  markDataUpdated(): void {
    if (this.dialogReturnDataAction == DataAction.DISCARD) {
      this.dialogReturnDataAction = DataAction.EDIT;
    }
  }

  ngOnDestroy(): void {
    console.log('BaseFormComponent ngOnDestroy');
    this._destroying$.next(undefined);
    this._destroying$.complete();

    if (!this.isDialogMode) {
      return;
    }

    // 開在 Dialog 裡的單檔或雙檔才會有這個問題。
    // 因為 New 或 Edit 儲存後不會直接呼叫 closeDialog 關閉，而是按 X 的時候才會關閉。
    // 按 X 關閉會先觸發一次父畫面的 Dialog onClose 事件，
    // 但是因為按 X 沒有地方可以攔截事件，所以不會塞 DialogReturnData 回去，
    // 所以父畫面的 onClose 抓不到資料可以做事情。
    // 然後才會再執行這個畫面的 ngOnDestroy，所以在這判斷再呼叫一次 closeDialog 把資料回傳。
    // 如果未來 New 或 Edit 儲存後就直接呼叫 closeDialog 關閉視窗的話，這整個都可以拿掉不做。
    if (this.hasMasterMenuBar &&
      (this.dialogReturnDataAction == DataAction.NEW
         || this.dialogReturnDataAction == DataAction.EDIT)) {
      console.log('BaseFormComponent ngOnDestroy closeDialog');
      this.closeDialog();
    }

    setTimeout(() => {
      // 清除所有 toast 提示訊息
      this.systemMessageService.clear();
    }, 0);
  }
}
